'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session } from '@supabase/supabase-js';
import { createClient } from '@/lib/supabase/client';

export interface AuthUser {
  id: string;
  email: string;
  role?: string;
  full_name?: string;
  company_name?: string;
}

export interface UserProfile {
  id: string;
  email: string;
  role: string;
  full_name?: string;
  company_name?: string;
  avatar_url?: string;
  phone?: string;
  bio?: string;
  location?: string;
  created_at?: string;
  updated_at?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  isAdmin: boolean;
  isAuthenticated: boolean;
  session: Session | null;
  isConfigured: boolean;
  userProfile: UserProfile | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isConfigured] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  const [supabase] = useState(() => createClient());

  // ✅ CONSOLIDATED: Single useEffect for all initialization and auth state management
  useEffect(() => {

    const loadUserProfile = async (userId: string): Promise<string | null> => {
      try {
        const { data: profile, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        if (error) {
          console.error('[AuthContext] Error loading user profile:', error);
          return null;
        }

        if (profile) {
          setUserProfile({
            id: profile.id as string,
            email: profile.email as string,
            role: profile.role as string,
            full_name: profile.full_name as string | undefined,
            company_name: profile.company_name as string | undefined,
            avatar_url: profile.avatar_url as string | undefined,
            phone: profile.phone as string | undefined,
            bio: profile.bio as string | undefined,
            location: profile.location as string | undefined,
            created_at: profile.created_at as string | undefined,
            updated_at: profile.updated_at as string | undefined
          });
          return profile.role as string;
        }
      } catch (error) {
        console.error('[AuthContext] Error loading user profile:', error);
      }
      return null;
    };

    const processSession = async (currentSession: Session | null) => {
      if (!currentSession) {
        setUser(null);
        setSession(null);
        setUserProfile(null);
        setLoading(false);
        return;
      }

      setSession(currentSession);

      // Check app_metadata.role FIRST (where Supabase stores admin role)
      let userRole = currentSession.user.app_metadata?.role ||
                     currentSession.user.user_metadata?.role;

      // ALWAYS load user profile from database for authenticated users
      // This ensures userProfile is populated regardless of metadata role
      const profileRole = await loadUserProfile(currentSession.user.id);

      // Use profile role if available, otherwise fall back to metadata
      if (!userRole && profileRole) {
        userRole = profileRole;
      }

      // If still no role and user_metadata has signup data, create profile
      if (!userRole && currentSession.user.user_metadata?.role) {
        console.log('[AuthContext] Creating profile from user_metadata for magic link user');
        const metadata = currentSession.user.user_metadata;

        // Import is at top, use upsertUserProfile from authService
        const { createClient } = await import('@/lib/supabase/client');
        const supabase = createClient();

        const { error: upsertError } = await supabase
          .from('user_profiles')
          .upsert({
            id: currentSession.user.id,
            email: currentSession.user.email!,
            full_name: metadata.full_name,
            role: metadata.role,
            company_name: metadata.company_name,
            updated_at: new Date().toISOString(),
          });

        if (!upsertError) {
          console.log('[AuthContext] Profile created from user_metadata');
          userRole = metadata.role;
          // Reload profile to populate userProfile state
          await loadUserProfile(currentSession.user.id);
        } else {
          console.error('[AuthContext] Failed to create profile from metadata:', upsertError);
        }
      }

      setUser({
        id: currentSession.user.id,
        email: currentSession.user.email!,
        role: userRole,
        full_name: currentSession.user.user_metadata?.full_name,
        company_name: currentSession.user.user_metadata?.company_name,
      });

      setLoading(false);
    };

    // Initialize session
    supabase.auth.getSession()
      .then(({ data: { session: initialSession } }) => {
        processSession(initialSession);
      })
      .catch((error) => {
        console.error('[AuthContext] Failed to load session:', error);
        setUser(null);
        setSession(null);
        setUserProfile(null);
        setLoading(false);
      });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (event === 'SIGNED_IN' && newSession) {
        await processSession(newSession);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setSession(null);
        setUserProfile(null);
      } else if (event === 'TOKEN_REFRESHED' && newSession) {
        setSession(newSession);
      }
    });

    // Session refresh interval (every 5 minutes)
    const refreshInterval = setInterval(async () => {
      try {
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        if (!error && currentSession) {
          setSession(currentSession);
        }
      } catch (error) {
        console.error('[AuthContext] Session refresh error:', error);
      }
    }, 5 * 60 * 1000);

    return () => {
      subscription.unsubscribe();
      clearInterval(refreshInterval);
    };
  }, [supabase]);

  const refreshAuth = async () => {
    try {
      const { data: { session: refreshedSession }, error } = await supabase.auth.refreshSession();
      
      if (error) throw error;
      
      if (refreshedSession) {
        setSession(refreshedSession);

        let userRole = refreshedSession.user.app_metadata?.role || 
                       refreshedSession.user.user_metadata?.role;
        
        if (!userRole) {
          const { data: profile, error } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('id', refreshedSession.user.id)
            .maybeSingle();

          if (!error && profile) {
            userRole = profile.role as string;
            setUserProfile({
              id: profile.id as string,
              email: profile.email as string,
              role: profile.role as string,
              full_name: profile.full_name as string | undefined,
              company_name: profile.company_name as string | undefined,
              avatar_url: profile.avatar_url as string | undefined,
              phone: profile.phone as string | undefined,
              bio: profile.bio as string | undefined,
              location: profile.location as string | undefined,
              created_at: profile.created_at as string | undefined,
              updated_at: profile.updated_at as string | undefined
            });
          }
        }
        
        setUser({
          id: refreshedSession.user.id,
          email: refreshedSession.user.email!,
          role: userRole,
          full_name: refreshedSession.user.user_metadata?.full_name,
          company_name: refreshedSession.user.user_metadata?.company_name,
        });
      } else {
        setUser(null);
        setSession(null);
        setUserProfile(null);
      }
    } catch (error) {
      console.error('[AuthContext] Failed to refresh auth:', error);
      setUser(null);
      setSession(null);
      setUserProfile(null);
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      setUserProfile(null);
    } catch (error) {
      console.error('[AuthContext] Sign out failed:', error);
    }
  };

  const isAdmin = user?.role === 'admin';
  const isAuthenticated = user !== null && session !== null;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signOut,
        refreshAuth,
        isAdmin,
        isAuthenticated,
        session,
        isConfigured,
        userProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}